import { SeededRandom } from '@/core/Random';
import { SWATCH_TEXELS, type SurfaceSwatch } from '@/terrain/TerrainSurface';
import { shade, type TextureRecipe } from './TextureRecipe';

/**
 * Turning an image into a ground swatch.
 *
 * Three jobs, and the middle one is the interesting one:
 *
 * 1. **Normalise.** Everything is resampled to {@link SWATCH_TEXELS} square, whatever arrived —
 *    a 1024-pixel render from the image model, a photograph the user dragged in. That is what lets
 *    every swatch share one GPU array texture.
 * 2. **Close the seam.** Nothing that arrives here tiles. An image model asked for a seamless
 *    texture produces something that looks seamless and is not, and a photograph certainly is not.
 *    See {@link closeSeam}.
 * 3. **Measure.** The average colour comes out of the pixels rather than out of the model's opinion
 *    of them, so the palette chip and the shader's no-swatch fallback agree with what is drawn.
 */

/** A swatch plus what was measured off it. */
export interface NormalizedSwatch {
  swatch: SurfaceSwatch;
  /** The mean of every texel, `#rrggbb`. What the chip and the brush ring draw. */
  averageColor: string;
}

export class SwatchImageError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SwatchImageError';
  }
}

/** Largest upload accepted, before decoding. A ground swatch is not a 40-megapixel photograph. */
export const MAX_SWATCH_BYTES = 12 * 1024 * 1024;

/**
 * Blob → a normalised, seam-closed swatch.
 *
 * `seamBand` of 0 skips the seam work, which is what an upload the user has *told* us already tiles
 * should get: blending the border of an already-seamless texture is a small, pointless smudge.
 */
export async function normalizeSwatch(
  source: Blob,
  tileMeters: number,
  options: { seamBand?: number } = {},
): Promise<NormalizedSwatch> {
  if (source.size > MAX_SWATCH_BYTES) {
    throw new SwatchImageError(
      `That image is larger than ${Math.round(MAX_SWATCH_BYTES / 1024 / 1024)} MB. Downscale it and try again.`,
    );
  }

  const url = URL.createObjectURL(source);
  try {
    const image = await loadImage(url);
    const context = createContext();
    // Cover-crop to a square first: stretching a 16:9 photograph into a square swatch makes every
    // gravel chip an ellipse, which reads as wrong long before anyone works out why.
    const side = Math.min(image.width, image.height);
    context.drawImage(
      image,
      (image.width - side) / 2,
      (image.height - side) / 2,
      side,
      side,
      0,
      0,
      SWATCH_TEXELS,
      SWATCH_TEXELS,
    );

    const pixels = context.getImageData(0, 0, SWATCH_TEXELS, SWATCH_TEXELS);
    const band = options.seamBand ?? Math.round(SWATCH_TEXELS * 0.12);
    if (band > 0) closeSeam(pixels, band);
    context.putImageData(pixels, 0, 0);

    return {
      swatch: {
        dataUrl: context.canvas.toDataURL('image/png'),
        tileMeters,
      },
      averageColor: averageColor(pixels.data),
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Make an image tile against itself by cross-fading its borders with the opposite edge.
 *
 * For a band `b` wide at each edge, each pixel is mixed with its wrapped counterpart on the far side
 * by a weight that ramps from ½ at the very edge to 0 at depth `b`. At the edge itself both sides
 * therefore compute the same ½/½ average, so the left column equals the right column and the top row
 * equals the bottom — which is exactly the condition for the image to tile.
 *
 * It costs a soft band of reduced contrast at the borders. That is a real cost, and it is much
 * cheaper than the alternative: an un-closed seam repeats every tile, so on a driveway it draws a
 * visible grid of straight lines across the ground, and a grid is far more legible to the eye than a
 * slight loss of detail. Mirroring would avoid the softening and introduce butterfly symmetry, which
 * on a large paved area is worse than either.
 */
export function closeSeam(image: ImageData, band: number): void {
  const { width, height, data } = image;
  const depth = Math.max(1, Math.min(band, Math.floor(Math.min(width, height) / 2) - 1));
  const source = new Uint8ClampedArray(data);

  const at = (x: number, y: number): number => (y * width + x) * 4;
  const blend = (dst: number, other: number, weight: number): void => {
    for (let c = 0; c < 3; c += 1) {
      const a = source[dst + c] ?? 0;
      const b = source[other + c] ?? 0;
      data[dst + c] = Math.round(a * (1 - weight) + b * weight);
    }
  };

  // Horizontal edges, then vertical, reading from the same untouched snapshot both times so the
  // second pass cannot compound the first — the corners are the only pixels both touch, and they
  // must end up agreeing with all four sides.
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < depth; x += 1) {
      const weight = 0.5 * (1 - x / depth);
      blend(at(x, y), at(width - 1 - x, y), weight);
      blend(at(width - 1 - x, y), at(x, y), weight);
    }
  }
  source.set(data);
  for (let x = 0; x < width; x += 1) {
    for (let y = 0; y < depth; y += 1) {
      const weight = 0.5 * (1 - y / depth);
      blend(at(x, y), at(x, height - 1 - y), weight);
      blend(at(x, height - 1 - y), at(x, y), weight);
    }
  }
}

/** Mean colour of RGBA bytes as `#rrggbb`. Alpha-weighted, so a padded image is not dragged white. */
export function averageColor(data: Uint8ClampedArray | Uint8Array): string {
  let r = 0;
  let g = 0;
  let b = 0;
  let weight = 0;
  for (let i = 0; i < data.length; i += 4) {
    const a = (data[i + 3] ?? 255) / 255;
    r += (data[i] ?? 0) * a;
    g += (data[i + 1] ?? 0) * a;
    b += (data[i + 2] ?? 0) * a;
    weight += a;
  }
  if (weight === 0) return '#888888';
  const hex = (value: number): string =>
    Math.round(Math.min(255, value / weight))
      .toString(16)
      .padStart(2, '0');
  return `#${hex(r)}${hex(g)}${hex(b)}`;
}

/**
 * Draw a swatch from the recipe's numbers alone — no image model involved.
 *
 * Used when there is no API key, when the image call fails, and in the reconstruction pipeline for
 * materials that are a small share of the site and not worth a paid render. It is frankly stylised
 * next to a photographic swatch, but it is *correct*: the colour, the grain size and the density all
 * come from the same grounded recipe the photographic path would have used, so a site painted with
 * these reads properly even though it does not photograph properly.
 *
 * Seeded from the recipe's own name so the same material always draws the same swatch — a material
 * that changed appearance every time a project was reopened would be indistinguishable from a bug.
 */
export function synthesizeSwatch(recipe: TextureRecipe): NormalizedSwatch {
  const context = createContext();
  const random = new SeededRandom(hashString(recipe.name));

  context.fillStyle = recipe.color;
  context.fillRect(0, 0, SWATCH_TEXELS, SWATCH_TEXELS);

  const palette = recipe.accents.length > 0 ? recipe.accents : [shade(recipe.color, -0.2)];
  // Grains per side, from the recipe's own scale. Clamped so a 60 cm paver does not become four
  // enormous blobs and a sand grain does not become half a million draw calls.
  const across = clamp(Math.round(recipe.tileMeters / Math.max(1e-4, recipe.grainMeters)), 4, 120);
  const grainPx = SWATCH_TEXELS / across;
  // Overdrawn ×2.5 so grains overlap and the base colour shows through as the gaps between them
  // rather than as a flat backdrop with confetti on it.
  const count = Math.round(across * across * 2.5);

  context.globalAlpha = 0.75;
  for (let i = 0; i < count; i += 1) {
    const x = random.next() * SWATCH_TEXELS;
    const y = random.next() * SWATCH_TEXELS;
    const radius = grainPx * random.range(0.32, 0.72);
    const color = palette[random.int(0, palette.length - 1)] ?? recipe.color;
    context.fillStyle = shade(color, random.range(-0.12, 0.12));
    // Drawn nine times on a 3×3 wrap so a grain that overhangs an edge reappears on the far side.
    // That is what makes the synthesized swatch genuinely seamless rather than seam-*closed*, and it
    // is why this path skips the blend band entirely.
    for (let dy = -1; dy <= 1; dy += 1) {
      for (let dx = -1; dx <= 1; dx += 1) {
        const cx = x + dx * SWATCH_TEXELS;
        const cy = y + dy * SWATCH_TEXELS;
        if (cx < -radius || cy < -radius || cx > SWATCH_TEXELS + radius || cy > SWATCH_TEXELS + radius) {
          continue;
        }
        context.beginPath();
        context.ellipse(cx, cy, radius, radius * random.range(0.7, 1), random.next() * Math.PI, 0, Math.PI * 2);
        context.fill();
      }
    }
  }
  context.globalAlpha = 1;

  const pixels = context.getImageData(0, 0, SWATCH_TEXELS, SWATCH_TEXELS);
  return {
    swatch: { dataUrl: context.canvas.toDataURL('image/png'), tileMeters: recipe.tileMeters },
    averageColor: averageColor(pixels.data),
  };
}

/** Whether this environment can make swatches at all. False under Node, where there is no canvas. */
export function canDrawSwatches(): boolean {
  return typeof document !== 'undefined' && typeof document.createElement === 'function';
}

function createContext(): CanvasRenderingContext2D {
  if (!canDrawSwatches()) {
    throw new SwatchImageError('Swatches need a browser canvas, which is not available here.');
  }
  const canvas = document.createElement('canvas');
  canvas.width = SWATCH_TEXELS;
  canvas.height = SWATCH_TEXELS;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) throw new SwatchImageError('Could not open a 2D canvas to build the swatch.');
  // Every swatch arrives larger than it is kept — 1024 from the image model, more from an upload —
  // so the resample in `normalizeSwatch` is the last chance to preserve the detail we are paying to
  // store. The default box filter aliases a gravel texture into a sparkle; this asks for the
  // multi-step downsample instead, which is a one-line difference in visibly cleaner grain.
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  return context;
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new SwatchImageError('That file could not be read as an image.'));
    image.src = url;
  });
}

function hashString(value: string): number {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
